import React, { useState } from "react";
import { Code, Database, LineChart, BarChart4 } from "lucide-react";

const InterviewPrepSection = () => {
  const [activeTab, setActiveTab] = useState("programming");

  const categories = [
    {
      id: "programming",
      label: "Programming",
      icon: <Code className="h-5 w-5" />,
    },
    {
      id: "database",
      label: "Database",
      icon: <Database className="h-5 w-5" />,
    },
    {
      id: "algorithms",
      label: "Algorithms",
      icon: <BarChart4 className="h-5 w-5" />,
    },
    {
      id: "system-design",
      label: "System Design",
      icon: <LineChart className="h-5 w-5" />,
    },
  ];

  const contentMap = {
    programming: {
      title: "Master Coding Interviews",
      topics: [
        "JavaScript Fundamentals",
        "React Concepts",
        "TypeScript Tips",
        "Python Essentials",
        "Java Programming",
        "Frontend Challenges",
      ],
      imageSrc:
        "https://images.pexels.com/photos/577585/pexels-photo-577585.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    },
    database: {
      title: "Database Interview Questions",
      topics: [
        "SQL Mastery",
        "NoSQL Concepts",
        "Database Design",
        "Query Optimization",
        "Indexing Strategies",
        "Transaction Management",
      ],
      imageSrc:
        "https://images.pexels.com/photos/113850/pexels-photo-113850.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    },
    algorithms: {
      title: "Algorithm & Data Structures",
      topics: [
        "Array Manipulation",
        "Graph Algorithms",
        "Dynamic Programming",
        "Tree Traversals",
        "Sorting Techniques",
        "Big O Analysis",
      ],
      imageSrc:
        "https://images.pexels.com/photos/8960464/pexels-photo-8960464.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    },
    "system-design": {
      title: "System Design for Engineers",
      topics: [
        "Scalability Principles",
        "Load Balancing",
        "Caching Strategies",
        "Microservices Architecture",
        "API Design",
        "Database Sharding",
      ],
      imageSrc:
        "https://images.pexels.com/photos/8960464/pexels-photo-8960464.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=",
    },
  };

  const activeContent = contentMap[activeTab];

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Prepare for Your Next Interview
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Access comprehensive interview preparation materials for various
            tech roles and topics.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center mb-12">
          <div className="bg-gray-100 p-1 rounded-xl flex flex-wrap justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveTab(category.id)}
                className={`px-5 py-2.5 rounded-lg flex items-center text-sm font-medium md:text-base mr-1 mb-1 md:mb-0 ${
                  activeTab === category.id
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <span className="mr-2">{category.icon}</span>
                {category.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Column (Topics) */}
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div>
              <h3 className="text-2xl font-bold mb-6">{activeContent.title}</h3>
              <ul className="space-y-4">
                {activeContent.topics.map((topic, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 p-1 bg-blue-50 rounded-full mr-3 mt-1">
                      <div className="bg-blue-600 rounded-full w-3 h-3"></div>
                    </div>
                    <div>
                      <h4 className="font-medium text-lg">{topic}</h4>
                      <p className="text-gray-600 mt-1">
                        {index % 2 === 0
                          ? `Master ${topic.toLowerCase()} with our comprehensive guides and practice questions.`
                          : `Understand key concepts in ${topic.toLowerCase()} through examples and exercises.`}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
              <button className="mt-8 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center">
                View All Resources
                <svg
                  className="w-4 h-4 ml-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 12H19M19 12L12 5M19 12L12 19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Right Column (Image) */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="rounded-xl overflow-hidden shadow-lg transform transition-transform duration-500 hover:scale-[1.02]">
              <img
                src={activeContent.imageSrc}
                alt={activeContent.title}
                className="w-full h-80 lg:h-96 object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InterviewPrepSection;
